import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { useOrgTimezone, formatDateTimeInTz, formatLocalDateInTz } from '../../utils/timezone';
import xeroPayrollService, {
  type MappableLeaveType,
  type XeroLeaveReadiness,
  type XeroLeaveType,
} from '../../services/xeroPayroll';
import { AlertTriangle, CalendarClock, CheckCircle2, RefreshCw } from 'lucide-react';
import XeroTilAccruals from './XeroTilAccruals';

/**
 * The leave part of Accounts → Xero: make Xero the source of truth for leave.
 *
 * Map our leave types to Xero's, then switch balances over. From then on,
 * approved leave is posted to Xero as leave applications (with the timesheet,
 * when payroll approves the person), balances are read back from Xero, and
 * FactreeTech stops accruing or hand-editing those types for linked employees.
 * Unpaid leave always stays in FactreeTech.
 *
 * Time in lieu is optional (2026-10-08). Mapped, time in lieu taken goes to
 * Xero as leave (its own line on the payslip), banked hours are added to the
 * employee's draft payslip as a leave accrual, and the balance is read back
 * like the others. Unmapped, it stays in FactreeTech and is paid as ordinary
 * hours.
 */

const LEAVE_TYPES: { key: MappableLeaveType; label: string; required: boolean; hint?: string }[] = [
  { key: 'annual', label: 'Annual leave', required: true },
  { key: 'personal', label: 'Personal / sick leave', required: true },
  { key: 'long_service', label: 'Long service leave', required: false },
  { key: 'other', label: 'Other leave', required: false },
  {
    key: 'time_in_lieu', label: 'Time in lieu', required: false,
    hint: 'Create a "Time in Lieu" leave type in Xero, counted in hours, and choose it here. Time taken then shows as its own line on the payslip, and banked hours are added to the draft pay run.',
  },
];

const errorMessage = (e: unknown, fallback: string) => (e instanceof Error && e.message) || fallback;

interface Props {
  /** Xero organisation chosen; nothing here works before that. */
  enabled: boolean;
  /** Called after the source changes, so the page can refresh its status. */
  onSourceChanged?: () => void;
}

export default function XeroLeaveSettings({ enabled, onSourceChanged }: Props) {
  const orgTz = useOrgTimezone();
  const [leaveTypes, setLeaveTypes] = useState<XeroLeaveType[] | null>(null);
  const [saved, setSaved] = useState<Record<string, string | null>>({});
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [readiness, setReadiness] = useState<XeroLeaveReadiness | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<'map' | 'source' | 'sync' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showUnlinked, setShowUnlinked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [types, map, ready] = await Promise.all([
        xeroPayrollService.listLeaveTypes(),
        xeroPayrollService.getLeaveTypeMap(),
        xeroPayrollService.getLeaveReadiness(),
      ]);
      if (types.success && types.data) setLeaveTypes(types.data);
      if (map.success && map.data) {
        const m = Object.fromEntries(map.data.map(x => [x.leave_type, x.xero_leave_type_id]));
        setSaved(m);
        setDraft(m);
      }
      if (ready.success && ready.data) setReadiness(ready.data);
    } catch (e: unknown) {
      setError(errorMessage(e, 'Could not read leave types from Xero.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  const dirty = useMemo(
    () => LEAVE_TYPES.some(t => (draft[t.key] ?? null) !== (saved[t.key] ?? null)),
    [draft, saved]
  );

  const isXero = readiness?.leave_source === 'xero';

  const saveMap = async () => {
    setBusy('map');
    setError(null);
    setInfo(null);
    try {
      const res = await xeroPayrollService.saveLeaveTypeMap(
        LEAVE_TYPES.map(t => ({ leave_type: t.key, xero_leave_type_id: draft[t.key] || null }))
      );
      if (res.success && res.data) {
        const m = Object.fromEntries(res.data.map(x => [x.leave_type, x.xero_leave_type_id]));
        setSaved(m);
        setDraft(m);
        setInfo('Leave types saved.');
      }
      const ready = await xeroPayrollService.getLeaveReadiness();
      if (ready.success && ready.data) setReadiness(ready.data);
    } catch (e: unknown) {
      setError(errorMessage(e, 'Could not save leave types.'));
    } finally {
      setBusy(null);
    }
  };

  const setSource = async (source: 'local' | 'xero') => {
    const prompt = source === 'xero'
      ? 'Read leave balances from Xero from now on?\n\n' +
        'Linked employees\' annual, personal and other mapped leave will show Xero\'s balance. ' +
        'FactreeTech stops accruing those types and they can only be changed in Xero. ' +
        (saved.time_in_lieu ? 'Time in lieu too, since it is mapped.' : 'Time in lieu stays in FactreeTech until it is mapped.')
      : 'Go back to calculating leave balances in FactreeTech?\n\n' +
        'The last figures read from Xero become the starting point, and accrual resumes from the next approved timesheet.';
    if (!window.confirm(prompt)) return;
    setBusy('source');
    setError(null);
    setInfo(null);
    try {
      const res = await xeroPayrollService.setLeaveSource(source);
      if (res.success && res.data) {
        setReadiness(res.data);
        const sync = res.data.sync;
        setInfo(source === 'xero'
          ? `Leave balances now come from Xero.${sync ? ` Read ${sync.synced} employee${sync.synced === 1 ? '' : 's'}${sync.failed.length ? `, ${sync.failed.length} failed` : ''}.` : ''}`
          : 'Leave balances are calculated in FactreeTech again.');
      }
      onSourceChanged?.();
    } catch (e: unknown) {
      setError(errorMessage(e, 'Could not change where balances come from.'));
    } finally {
      setBusy(null);
    }
  };

  const syncNow = async () => {
    setBusy('sync');
    setError(null);
    setInfo(null);
    try {
      const res = await xeroPayrollService.syncLeaveBalances();
      if (res.success && res.data) {
        const { synced, failed } = res.data;
        setInfo(`Read balances for ${synced} employee${synced === 1 ? '' : 's'} from Xero${failed.length ? `; ${failed.length} failed` : ''}.`);
      }
      const ready = await xeroPayrollService.getLeaveReadiness();
      if (ready.success && ready.data) setReadiness(ready.data);
    } catch (e: unknown) {
      setError(errorMessage(e, 'Could not read balances from Xero.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-primary-600 shrink-0" />
              Leave balances from Xero
            </h3>
            <p className="text-sm text-neutral-500 mt-1">
              Match leave types, then let Xero be the source of truth. Approved leave is sent to Xero as
              leave applications when payroll approves each person, and balances are read back after each
              pay run and nightly.
            </p>
          </div>
          {readiness && (
            isXero ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
                <CheckCircle2 className="h-3.5 w-3.5" /> From Xero
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-700 text-xs font-medium">
                Calculated in FactreeTech
              </span>
            )
          )}
        </div>

        {!enabled ? (
          <p className="text-sm text-neutral-500 break-words">Connect Xero and choose its organisation above first.</p>
        ) : loading && !leaveTypes ? (
          <p className="text-sm text-neutral-500">Reading leave types from Xero…</p>
        ) : (
          <div className="space-y-5">
            {error && <p className="text-sm text-red-600 break-words">{error}</p>}
            {info && <p className="text-sm text-emerald-700 break-words">{info}</p>}

            <ul className="divide-y divide-neutral-200 border border-neutral-200 rounded-lg">
              {LEAVE_TYPES.map(t => (
                <li key={t.key} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <div className="sm:w-2/5 min-w-0">
                    <p className="font-medium text-neutral-900">{t.label}</p>
                    <p className="text-xs text-neutral-500">{t.required ? 'Required' : 'Optional'}</p>
                    {t.hint && <p className="text-xs text-neutral-500 mt-1 break-words">{t.hint}</p>}
                  </div>
                  <select
                    aria-label={`Xero leave type for ${t.label}`}
                    value={draft[t.key] ?? ''}
                    onChange={e => setDraft(prev => ({ ...prev, [t.key]: e.target.value || null }))}
                    className="flex-1 min-w-0 w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm bg-white"
                  >
                    <option value="">— Not mapped —</option>
                    {(leaveTypes ?? []).map(x => (
                      <option key={x.leave_type_id} value={x.leave_type_id}>
                        {x.name}{x.type_of_units && !/hour/i.test(x.type_of_units) ? ` (${x.type_of_units})` : ''}
                      </option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              <Button onClick={saveMap} disabled={!dirty || busy !== null}>
                {busy === 'map' ? 'Saving…' : dirty ? 'Save leave types' : 'No changes'}
              </Button>
              <Button variant="ghost" onClick={() => void load()} disabled={loading} className="flex items-center gap-1">
                <RefreshCw className="h-4 w-4" />
                Reload from Xero
              </Button>
            </div>

            {saved.time_in_lieu && (
              <XeroTilAccruals canAccrue={readiness?.can_accrue_til !== false} orgTz={orgTz} />
            )}

            {readiness && readiness.unlinked_users.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
                <p className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span className="min-w-0">
                    {readiness.unlinked_users.length} {readiness.unlinked_users.length === 1 ? 'person is' : 'people are'} not
                    linked to a Xero employee (Match employees, above). They keep FactreeTech's balances and cannot be posted to Xero.{' '}
                    <button type="button" className="underline font-medium" onClick={() => setShowUnlinked(v => !v)}>
                      {showUnlinked ? 'Hide' : 'Show'}
                    </button>
                  </span>
                </p>
                {showUnlinked && (
                  <p className="mt-2 break-words">{readiness.unlinked_users.map(u => u.name).join(', ')}</p>
                )}
              </div>
            )}

            {readiness && (
              <div className="pt-4 border-t border-neutral-200 space-y-3">
                {isXero ? (
                  <>
                    <p className="text-sm text-neutral-700">
                      Balances come from Xero
                      {readiness.leave_source_since ? ` since ${formatLocalDateInTz(readiness.leave_source_since.slice(0, 10), orgTz)}` : ''}.
                      {' '}Last read {readiness.last_balance_sync_at ? formatDateTimeInTz(readiness.last_balance_sync_at, orgTz) : 'never'}.
                    </p>
                    {readiness.last_balance_sync_error && (
                      <p className="text-sm text-red-600 break-words">Last read had problems: {readiness.last_balance_sync_error}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={syncNow} disabled={busy !== null} className="flex items-center gap-1">
                        <RefreshCw className="h-4 w-4" />
                        {busy === 'sync' ? 'Reading from Xero…' : 'Refresh balances from Xero'}
                      </Button>
                      <Button variant="outline" onClick={() => void setSource('local')} disabled={busy !== null}>
                        Calculate in FactreeTech instead
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    {!readiness.ready && (
                      <p className="text-sm text-neutral-600">
                        Map {readiness.unmapped_leave_types.map(t => LEAVE_TYPES.find(x => x.key === t)?.label.toLowerCase() ?? t).join(' and ')} first.
                      </p>
                    )}
                    <Button onClick={() => void setSource('xero')} disabled={!readiness.ready || dirty || busy !== null}>
                      {busy === 'source' ? 'Reading balances from Xero…' : 'Use Xero for leave balances'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
